/**
 * AudioManager — HST Game Audio Engine
 * Pure Web Audio API synthesis, zero external files.
 */

let audioCtx = null;
let masterGain = null;
let ambientNodes = null;

const getCtx = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = 0.5;
    masterGain.connect(audioCtx.destination);
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

const getMaster = () => {
  getCtx();
  return masterGain;
};

// --- Helper: play a simple tone ---
const playTone = (freq, duration, type = 'sine', gainVal = 0.3, delay = 0) => {
  const ctx = getCtx();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(gainVal, ctx.currentTime + delay);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + duration);
  osc.connect(gain);
  gain.connect(getMaster());
  osc.start(ctx.currentTime + delay);
  osc.stop(ctx.currentTime + delay + duration + 0.05);
};

// --- Helper: play a noise burst (for percussive sounds) ---
const playNoise = (duration, gainVal = 0.15, filterFreq = 4000) => {
  const ctx = getCtx();
  const bufferSize = ctx.sampleRate * duration;
  const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = Math.random() * 2 - 1;
  }
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const filter = ctx.createBiquadFilter();
  filter.type = 'highpass';
  filter.frequency.value = filterFreq;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(gainVal, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
  noise.connect(filter);
  filter.connect(gain);
  gain.connect(getMaster());
  noise.start();
  noise.stop(ctx.currentTime + duration + 0.05);
};

// ========== SOUND EFFECTS ==========

export const sounds = {
  /** Jour suivant — short tick/advance */
  nextDay: () => {
    playTone(880, 0.08, 'triangle', 0.25);
    playTone(660, 0.1, 'triangle', 0.15, 0.06);
  },

  /** Avance rapide — quick ascending arpeggio */
  fastForward: () => {
    playTone(523, 0.06, 'square', 0.12);
    playTone(659, 0.06, 'square', 0.12, 0.05);
    playTone(784, 0.06, 'square', 0.12, 0.10);
    playTone(1047, 0.08, 'square', 0.15, 0.15);
  },

  /** Contrat signé — success chime (C major arpeggio) */
  contractSign: () => {
    playTone(523, 0.15, 'sine', 0.2);
    playTone(659, 0.15, 'sine', 0.2, 0.12);
    playTone(784, 0.2, 'sine', 0.25, 0.24);
  },

  /** Notification — soft bell ding */
  notification: () => {
    playTone(1200, 0.3, 'sine', 0.2);
    playTone(1800, 0.15, 'sine', 0.08, 0.02);
  },

  /** Nouveau message Messagerie — deux notes ascendantes type "ding" inbox iOS/macOS */
  inboxDing: () => {
    playTone(1568, 0.18, 'sine', 0.18);              // G6
    playTone(2093, 0.22, 'sine', 0.14, 0.08);        // C7 — un peu décalé
    playTone(2637, 0.35, 'triangle', 0.06, 0.14);    // E7 — petite traîne harmonique
  },

  /** Événement aléatoire — attention alert */
  event: () => {
    playTone(440, 0.12, 'sawtooth', 0.15);
    playTone(550, 0.12, 'sawtooth', 0.15, 0.13);
    playTone(440, 0.12, 'sawtooth', 0.1, 0.26);
  },

  /** Succès débloqué — triumphant fanfare */
  achievement: () => {
    playTone(523, 0.12, 'sine', 0.25);
    playTone(659, 0.12, 'sine', 0.25, 0.1);
    playTone(784, 0.12, 'sine', 0.25, 0.2);
    playTone(1047, 0.3, 'sine', 0.3, 0.3);
    playNoise(0.08, 0.06, 6000);
  },

  /** Achat véhicule — ka-ching */
  buy: () => {
    playNoise(0.04, 0.1, 5000);
    playTone(1500, 0.08, 'sine', 0.2, 0.03);
    playTone(2000, 0.15, 'sine', 0.15, 0.08);
  },

  /** Vente véhicule — lower pitch cash */
  sell: () => {
    playTone(800, 0.1, 'triangle', 0.2);
    playTone(1000, 0.12, 'triangle', 0.15, 0.08);
    playNoise(0.05, 0.08, 4000);
  },

  /** Recrutement chauffeur */
  hire: () => {
    playTone(440, 0.1, 'sine', 0.2);
    playTone(554, 0.1, 'sine', 0.2, 0.08);
    playTone(659, 0.15, 'sine', 0.22, 0.16);
  },

  /** Formation terminée */
  training: () => {
    playTone(659, 0.1, 'sine', 0.18);
    playTone(784, 0.1, 'sine', 0.18, 0.08);
    playTone(988, 0.2, 'sine', 0.22, 0.16);
  },

  /** Clic UI générique */
  click: () => {
    playTone(700, 0.04, 'triangle', 0.12);
  },

  /** Erreur / action impossible */
  error: () => {
    playTone(350, 0.12, 'sawtooth', 0.15);
    playTone(280, 0.15, 'sawtooth', 0.12, 0.1);
  },

  /** Bilan mensuel */
  monthlyReport: () => {
    playTone(440, 0.1, 'sine', 0.15);
    playTone(554, 0.1, 'sine', 0.15, 0.1);
    playTone(659, 0.1, 'sine', 0.15, 0.2);
    playTone(880, 0.25, 'sine', 0.2, 0.3);
  },
};

// ========== AMBIENT MUSIC ==========

export const startAmbient = () => {
  if (ambientNodes) return; // Already playing
  const ctx = getCtx();

  // Create a gentle generative ambient pad
  const masterAmbGain = ctx.createGain();
  masterAmbGain.gain.value = 0.08;
  masterAmbGain.connect(getMaster());

  // Low drone
  const drone = ctx.createOscillator();
  drone.type = 'sine';
  drone.frequency.value = 110; // A2
  const droneGain = ctx.createGain();
  droneGain.gain.value = 0.4;
  drone.connect(droneGain);
  droneGain.connect(masterAmbGain);

  // Mid pad — fifth above
  const pad = ctx.createOscillator();
  pad.type = 'sine';
  pad.frequency.value = 165; // E3
  const padGain = ctx.createGain();
  padGain.gain.value = 0.25;
  pad.connect(padGain);
  padGain.connect(masterAmbGain);

  // High shimmer
  const shimmer = ctx.createOscillator();
  shimmer.type = 'sine';
  shimmer.frequency.value = 330; // E4
  const shimmerGain = ctx.createGain();
  shimmerGain.gain.value = 0.1;
  shimmer.connect(shimmerGain);
  shimmerGain.connect(masterAmbGain);

  // LFO for slow movement
  const lfo = ctx.createOscillator();
  lfo.type = 'sine';
  lfo.frequency.value = 0.15; // Very slow
  const lfoGain = ctx.createGain();
  lfoGain.gain.value = 3;
  lfo.connect(lfoGain);
  lfoGain.connect(drone.frequency);

  const lfo2 = ctx.createOscillator();
  lfo2.type = 'sine';
  lfo2.frequency.value = 0.08;
  const lfo2Gain = ctx.createGain();
  lfo2Gain.gain.value = 5;
  lfo2.connect(lfo2Gain);
  lfo2Gain.connect(pad.frequency);

  // Gentle filter on the pad
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 800;
  filter.Q.value = 1;
  padGain.disconnect();
  padGain.connect(filter);
  filter.connect(masterAmbGain);

  // Filter LFO
  const filterLfo = ctx.createOscillator();
  filterLfo.type = 'sine';
  filterLfo.frequency.value = 0.05;
  const filterLfoGain = ctx.createGain();
  filterLfoGain.gain.value = 300;
  filterLfo.connect(filterLfoGain);
  filterLfoGain.connect(filter.frequency);

  drone.start();
  pad.start();
  shimmer.start();
  lfo.start();
  lfo2.start();
  filterLfo.start();

  ambientNodes = {
    masterGain: masterAmbGain,
    oscillators: [drone, pad, shimmer, lfo, lfo2, filterLfo],
  };
};

export const stopAmbient = () => {
  if (!ambientNodes) return;
  const { masterGain: ambGain, oscillators } = ambientNodes;
  const ctx = getCtx();
  // Fade out
  ambGain.gain.setValueAtTime(ambGain.gain.value, ctx.currentTime);
  ambGain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);
  setTimeout(() => {
    oscillators.forEach(osc => {
      try { osc.stop(); } catch (e) { console.warn('Audio stop:', e.message); }
    });
    try { ambGain.disconnect(); } catch (e) { console.warn('Audio disconnect:', e.message); }
    ambientNodes = null;
  }, 600);
};

export const isAmbientPlaying = () => !!ambientNodes;

// ========== VOLUME CONTROL ==========

export const setMasterVolume = (val) => {
  // val: 0 to 1
  const gain = getMaster();
  gain.gain.setValueAtTime(Math.max(0, Math.min(1, val)), getCtx().currentTime);
};
